import { motion } from "motion/react";
import React from "react";
import { AnalyticsEvent } from "../../analytics/track";
import { useAnalytics } from "../../hooks/analytics";
import { useResultsOrder } from "../../hooks/results_order";
import ArticlesIcon from "../../icons/articles_icon";
import { isExactNear, isKeyword } from "../../lib/routing";
import { useConfig } from "../../providers/config_provider";
import { useSearchQuery } from "../../providers/search_query_provider";
import type { Article, ConversationTurnRouting } from "../../types";
import { SearchArticle } from "../content/article";
import SearchSection from "../section";
import type { SearchResultsSectionOrder } from "./data";

const VIEW_ALL_BUTTON_TEXT = "View All";

type SearchResultsArticlesProps = {
  routing?: ConversationTurnRouting;
  articles?: Article[];
  order: SearchResultsSectionOrder;
};

export function SearchResultsArticles({ routing, articles, order }: SearchResultsArticlesProps) {
  const { organization } = useConfig();
  const { strings } = organization;
  const { articleResultsHeader, citationsHeader } = strings;
  const { normalizedQuery } = useSearchQuery();
  const { conversationContext } = useConfig();
  const { fallbackSearchUrl } = conversationContext;
  const { animationVariants, className } = useResultsOrder(order);
  const { track } = useAnalytics();

  // Don't show the articles section if there are no articles
  if (!articles?.length) return null;

  // Dispaly parameters for this section based on routing
  const isExactNearRouting = isExactNear(routing);
  const icon = isExactNearRouting ? undefined : ArticlesIcon;
  const title = isExactNearRouting ? undefined : articleResultsHeader;
  const subtitle = isExactNearRouting ? citationsHeader : undefined;

  // Show the See All button for Keyword responses (when we have a fallbackSearchUrl)
  const shouldShowSeeAllButton = fallbackSearchUrl && normalizedQuery?.length && isKeyword(routing);
  const searchUrl = fallbackSearchUrl + encodeURIComponent(normalizedQuery);

  return (
    <motion.div
      key="data--articles"
      variants={animationVariants}
      initial="initial"
      animate="animate"
      exit="exit"
      className={className}
    >
      <SearchSection
        icon={icon}
        title={title}
        subtitle={subtitle}
        meta="articles"
      >
        {articles?.map((article: Article) => (
          <SearchArticle
            key={article.href}
            article={article}
          />
        ))}

        {shouldShowSeeAllButton && (
          <a
            href={searchUrl}
            className="dewey--search--articles--see-all"
            onClick={() => {
              track(AnalyticsEvent.SEARCH_RESULTS_CLICKED, {
                query: normalizedQuery,
                query_type: routing,
                result_type: "article",
                result_value: { rank: -1, text: VIEW_ALL_BUTTON_TEXT, href: searchUrl },
              });
            }}
          >
            {VIEW_ALL_BUTTON_TEXT}
          </a>
        )}
      </SearchSection>
    </motion.div>
  );
}
